import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, ShieldCheck, Clock, Star, Loader2, Check, ArrowLeft } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { getTrafficAttribution } from '../Analytics';
import { SelectedVehicleCard, vehicleLabel } from './SelectedVehicleCard';
import { QUAL_STEPS } from './qualSteps';
import { QualQuestion } from './QualQuestion';
import { PhoneCodeStep } from './PhoneCodeStep';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneOk = (p) => { const d = p.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, ''); return d.length === 10 && !'01'.includes(d[0]); };
const FIELDS = [
  ['firstName', 'Prénom', 'First name', 'text'],
  ['lastName', 'Nom', 'Last name', 'text'],
  ['email', 'Courriel', 'Email', 'email'],
  ['phone', 'Téléphone', 'Phone', 'tel'],
];
const CONTACT = QUAL_STEPS.length;
const AUTOCOMPLETE = { firstName: 'given-name', lastName: 'family-name', email: 'email', phone: 'tel' };
const CODE = CONTACT + 1;

export const QuickApplyPanel = ({ v, en, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [qual, setQual] = useState({});
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pick = (key, val) => {
    setQual({ ...qual, [key]: val });
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const sendLead = async (token) => {
    setStatus('sending');
    const a = getTrafficAttribution();
    try {
      await axios.post(`${API}/loan-requests`, {
        ...form,
        employmentStatus: qual.employmentType,
        annualIncome: qual.monthlyIncome,
        creditScore: qual.creditSituation,
        vehicleType: vehicleLabel(v, false),
        budgetRange: 'Véhicule choisi en inventaire',
        selectedVehicleId: v.id,
        selectedVehicleUrl: `${window.location.origin}/nos-vehicules/${v.id}`,
        qualification: qual,
        phoneVerificationToken: token || null,
        message: '',
        source: 'Website',
        traffic_source: a?.source || 'direct',
        traffic_medium: a?.medium || 'none',
        campaign: a?.campaign || null,
        keyword: a?.keyword || null,
        gclid: a?.gclid || null,
      });
      navigate('/merci');
    } catch {
      setStatus('error');
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(form.email)) { setStatus('bad-email'); return; }
    if (!phoneOk(form.phone)) { setStatus('bad-phone'); return; }
    setStatus('sending');
    try {
      await axios.post(`${API}/phone/send-code`, { phone: form.phone });
      setStatus('idle');
      setStep(CODE);
    } catch {
      sendLead(null);
    }
  };

  const progress = Math.min(step, CONTACT) / CONTACT;

  return (
    <aside className="lg:sticky lg:top-[164px] bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden flex flex-col animate-fade-up" data-testid="quick-apply-panel">
      <div className="bg-gradient-to-r from-[#1a5c3b] to-[#0f3d26] px-5 py-4 flex items-start justify-between text-white shrink-0">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-green-200">{en ? 'Financing application' : 'Demande de financement'}</p>
          <h2 className="text-xl font-black text-white mt-0.5">{en ? 'Finance Me!' : 'Financez-moi!'}</h2>
          <p className="text-xs text-white/80 mt-0.5" data-testid="quick-apply-step-label">
            {step < CONTACT ? `${en ? 'Question' : 'Question'} ${step + 1} / ${CONTACT}` : (step === CONTACT ? (en ? 'Last step: your contact info' : 'Dernière étape : vos coordonnées') : (en ? 'Confirm your phone' : 'Confirmez votre téléphone'))}
          </p>
        </div>
        <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/15 transition-colors" aria-label="Close" data-testid="quick-apply-close">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="h-1 bg-slate-100"><div className="h-full glow-border transition-[width] duration-500" style={{ width: `${Math.max(progress, 0.06) * 100}%` }} /></div>

      <div className="px-5 py-5">
        <SelectedVehicleCard v={v} en={en} onRemove={onClose} compact />

        {step < CONTACT && <QualQuestion step={QUAL_STEPS[step]} value={qual[QUAL_STEPS[step].key]} en={en} onPick={(val) => pick(QUAL_STEPS[step].key, val)} />}

        {step === CONTACT && (
          <form onSubmit={submitContact} className="space-y-3 animate-fade-up" data-testid="quick-apply-form">
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map(([k, fr, enL, type]) => (
                <div key={k} className="space-y-1">
                  <Label htmlFor={`qa-${k}`} className="text-xs font-semibold text-gray-700">{en ? enL : fr} *</Label>
                  <Input
                    id={`qa-${k}`}
                    type={type}
                    required
                    value={form[k]}
                    onChange={(e) => { setForm({ ...form, [k]: e.target.value }); if (status !== 'sending') setStatus('idle'); }}
                    className={`h-11 md:h-10 text-base md:text-sm rounded-lg bg-gray-50 ${(k === 'email' && status === 'bad-email') || (k === 'phone' && status === 'bad-phone') ? 'border-red-500 border-2' : ''}`}
                    autoComplete={AUTOCOMPLETE[k]}
                    data-testid={`quick-apply-${k}`}
                  />
                </div>
              ))}
            </div>
            {status === 'bad-email' && <p className="text-sm text-red-600" data-testid="quick-apply-email-error">{en ? 'Please enter a valid email address' : 'Veuillez entrer une adresse courriel valide'}</p>}
            {status === 'bad-phone' && <p className="text-sm text-red-600" data-testid="quick-apply-phone-error">{en ? 'Please enter a valid 10-digit phone number' : 'Veuillez entrer un numéro de téléphone valide à 10 chiffres'}</p>}
            {status === 'error' && <p className="text-sm text-red-600" data-testid="quick-apply-error">{en ? 'Error submitting, please try again.' : 'Erreur lors de la soumission, veuillez réessayer.'}</p>}
            <button type="submit" disabled={status === 'sending'} className="btn-shimmer w-full h-12 rounded-xl bg-[#1a5c3b] hover:bg-[#165432] text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-green-700/25 transition-colors disabled:opacity-70" data-testid="quick-apply-submit">
              {status === 'sending' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              {en ? 'Check my eligibility for free' : 'Vérifier mon admissibilité gratuitement'}
            </button>
            <p className="text-[11px] text-slate-400 text-center">{en ? 'We will text you a code to confirm your number.' : 'Nous vous enverrons un code par SMS pour confirmer votre numéro.'}</p>
          </form>
        )}

        {step === CODE && (
          <>
            <PhoneCodeStep phone={form.phone} en={en} busy={status === 'sending'} onVerified={(t) => sendLead(t)} onSkip={() => sendLead(null)} />
            {status === 'error' && <p className="text-sm text-red-600 mt-2" data-testid="quick-apply-error">{en ? 'Error submitting, please try again.' : 'Erreur lors de la soumission, veuillez réessayer.'}</p>}
          </>
        )}

        {step > 0 && step <= CODE && (
          <button type="button" onClick={() => { setStep(step - 1); setStatus('idle'); }} className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800" data-testid="quick-apply-back">
            <ArrowLeft className="w-3.5 h-3.5" />{en ? 'Back' : 'Retour'}
          </button>
        )}

        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
          {[[ShieldCheck, en ? '100% confidential' : '100% confidentiel'], [Clock, en ? 'Response in 24h' : 'Réponse en 24h'], [Star, '4.9 Google']].map(([Icon, t]) => (
            <div key={t} className="bg-slate-50 rounded-lg py-2 px-1">
              <Icon className="w-4 h-4 text-[#1a5c3b] mx-auto mb-1" />
              <p className="text-[11px] font-semibold text-slate-600">{t}</p>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
};
